import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AlternateEmail as AlternateEmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  Campaign as CampaignIcon,
  ContentCopy as ContentCopyIcon,
  FilterAlt as FilterAltIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Movie as MovieIcon,
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  OpenInFull as OpenInFullIcon,
  PlaylistAdd as PlaylistAddIcon,
  Repeat as RepeatIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import AppService from "../services/AppService";
import UGCVideoService from "../services/UGCVideoService";
import CreationProgressModal from "../components/CreationProgressModal";
import type { App } from "../types/app";
import type {
  CreateUGCCharacterRequest,
  CreateUGCVideoJobRequest,
  UGCVideoGender,
  UGCVideoCharacter,
  UGCVideoJob,
  UGCVideoProvider,
  UGCVideoWorkflow,
  UGCVideoReviewPayload,
} from "../types/ugcVideo";

const OBJECTIVE_OPTIONS = [
  "Increase followers",
  "Increase app installs",
  "Improve engagement rate",
  "Drive profile visits",
  "Increase website clicks",
  "Boost conversion rate",
  "Promote a feature launch",
  "Increase social shares",
  "Increase comments",
  "Increase likes",
  "Improve audience retention",
  "Drive sign-ups",
  "Generate waitlist leads",
  "Boost community participation",
  "Promote seasonal campaign",
  "Increase product awareness",
  "Increase trust with testimonials",
  "Drive app reactivation",
  "Increase demo requests",
  "Boost UGC remixes",
];

const HOOK_OPTIONS = [
  "5 things you should know",
  "I can't believe I'm saying this",
  "Stop scrolling if you...",
  "This changed everything for me",
  "Nobody talks about this",
  "You are doing this wrong",
  "I wish I knew this earlier",
  "POV: you finally found...",
  "Here is the truth about...",
  "Watch this before you...",
  "The biggest mistake people make",
  "I tested this so you don't have to",
  "Do this in the next 10 seconds",
  "I bet you didn't know this",
  "If I started over, I'd do this",
  "This is your sign to...",
  "Three secrets no one tells you",
  "I tried this for 7 days",
  "This hack is underrated",
  "The results shocked me",
];

const GENDER_OPTIONS: Array<{ value: UGCVideoGender; label: string }> = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non_binary", label: "Non-binary" },
];

const VIDEO_PROVIDER_OPTIONS: Array<{ value: UGCVideoProvider; label: string }> = [
  { value: "kling", label: "Kling AI" },
  { value: "gemini", label: "Google Veo (Gemini)" },
];

const VIDEO_WORKFLOW_OPTIONS: Array<{ value: UGCVideoWorkflow; label: string }> = [
  { value: "direct", label: "Direct (no reference frames)" },
  { value: "multi_frame", label: "Multi Frame (2 reference frames)" },
];

const DEFAULT_GEMINI_VIDEO_MODEL = "veo-3.1-fast-generate-preview";
const DEFAULT_KLING_VIDEO_MODEL = "kling-v2.6-std";

const GEMINI_VIDEO_MODELS: Array<{ value: string; label: string; costTag: string }> = [
  {
    value: "veo-3.1-fast-generate-preview",
    label: "Veo 3.1 Fast",
    costTag: "Google pricing (see your billing tier)",
  },
  {
    value: "veo-3.1-generate-preview",
    label: "Veo 3.1 Quality",
    costTag: "Google pricing (see your billing tier)",
  },
];

const KLING_VIDEO_MODELS: Array<{ value: string; label: string; costTag: string }> = [
  {
    value: "kling-v2.5-turbo",
    label: "Kling 2.5 Turbo",
    costTag: "~$0.070/s (std equivalent)",
  },
  {
    value: "kling-v2.6-std",
    label: "Kling 2.6 Standard",
    costTag: "~$0.056/s (std equivalent)",
  },
  {
    value: "kling-v2.6-pro",
    label: "Kling 2.6 Pro",
    costTag: "~$0.098/s (pro equivalent)",
  },
  {
    value: "kling-video-o1",
    label: "Kling O1",
    costTag: "~$0.126/s (std equivalent)",
  },
];

const CHARACTER_IMAGE_MODELS = [
  { value: "gemini-2.5-flash-image", label: "Gemini 2.5 Flash Image" },
  { value: "gpt-image-2.5-flare", label: "GPT Image 2.5 Flare" },
  { value: "gpt-image-2.5-sunburst", label: "GPT Image 2.5 Sunburst" },
];

type DurationOption = {
  seconds: number;
  native: boolean;
  note?: string;
};

function getDurationOptions(
  provider: UGCVideoProvider,
  model: string,
): DurationOption[] {
  if (provider === "kling") {
    const normalized = String(model || "").toLowerCase();
    if (normalized.includes("kling")) {
      return [
        { seconds: 5, native: true, note: "Native Kling duration" },
        { seconds: 10, native: true, note: "Native Kling duration" },
        {
          seconds: 15,
          native: false,
          note: "Uses Kling extension logic after native generation",
        },
        {
          seconds: 20,
          native: false,
          note: "Uses Kling extension logic after native generation",
        },
      ];
    }
    return [
      { seconds: 5, native: true },
      { seconds: 10, native: true },
      { seconds: 15, native: false },
      { seconds: 20, native: false },
    ];
  }

  return [
    { seconds: 4, native: true, note: "Native Veo duration" },
    { seconds: 6, native: true, note: "Native Veo duration" },
    { seconds: 8, native: true, note: "Native Veo duration" },
    {
      seconds: 12,
      native: false,
      note: "Uses Veo extension logic after native generation",
    },
    {
      seconds: 15,
      native: false,
      note: "Uses Veo extension logic after native generation",
    },
  ];
}

const JOB_PHASE_STEPS = ["Review Pending", "Queued", "Processing", "Completed"];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "review_pending", label: "Review Pending" },
  { value: "queued", label: "Queued" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

const DATE_FILTER_OPTIONS = [
  { value: "all", label: "All dates" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const CAPTION_PLATFORMS = [
  "tiktok",
  "instagram_reels",
  "youtube_shorts",
  "x",
  "linkedin",
  "facebook",
];

const PLATFORM_META: Record<
  string,
  {
    label: string;
    icon: React.ElementType;
    accent: string;
  }
> = {
  tiktok: { label: "TikTok", icon: MusicNoteIcon, accent: "#111111" },
  instagram_reels: {
    label: "Instagram Reels",
    icon: InstagramIcon,
    accent: "#E1306C",
  },
  youtube_shorts: {
    label: "YouTube Shorts",
    icon: YouTubeIcon,
    accent: "#FF0000",
  },
  x: { label: "X", icon: AlternateEmailIcon, accent: "#111111" },
  linkedin: { label: "LinkedIn", icon: LinkedInIcon, accent: "#0A66C2" },
  facebook: { label: "Facebook", icon: FacebookIcon, accent: "#1877F2" },
};

function statusColor(status: UGCVideoJob["status"]) {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "processing") return "info";
  if (status === "review_pending") return "secondary";
  return "warning";
}

function statusLabel(status: UGCVideoJob["status"]) {
  return status === "review_pending" ? "REVIEW_PENDING" : status.toUpperCase();
}

function estimateKlingCostPerSecond(model: string): number {
  const normalized = String(model || "").trim().toLowerCase();
  if (normalized === "kling-v2.5-turbo") return 0.07;
  if (normalized === "kling-v2.6-pro") return 0.098;
  if (normalized === "kling-video-o1") return 0.126;
  return 0.056;
}

function estimateJobCost(provider: UGCVideoProvider, model: string, seconds: number): string {
  if (provider !== "kling") return "Provider-priced";
  const total = estimateKlingCostPerSecond(model) * Math.max(1, Number(seconds || 0));
  return `~$${total.toFixed(2)}`;
}

function passesDateFilter(isoDate: string, filter: string): boolean {
  if (filter === "all") return true;
  const dateValue = new Date(isoDate).getTime();
  if (!Number.isFinite(dateValue)) return false;
  const days = filter === "7d" ? 7 : filter === "30d" ? 30 : 90;
  const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
  return dateValue >= threshold;
}

function getJobStepIndex(job: UGCVideoJob): number {
  if (job.status === "review_pending") return 0;
  if (job.status === "queued") return 1;
  if (job.status === "processing") return 2;
  if (job.status === "completed") return 3;
  return 2;
}

function textChanged(a: string, b: string): boolean {
  return String(a || "").trim() !== String(b || "").trim();
}

function planDateLabel(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function reviewFromJob(job: UGCVideoJob): UGCVideoReviewPayload {
  const planning = job.planning;
  const captionsByPlatform: Record<string, string[]> = {};
  CAPTION_PLATFORMS.forEach((platform) => {
    captionsByPlatform[platform] = Array.isArray(
      planning?.captionsByPlatform?.[platform],
    )
      ? [...(planning?.captionsByPlatform?.[platform] || [])]
      : [];
  });
  return {
    characterProfile: {
      name: String(planning?.characterProfile?.name || ""),
      ageRange: String(planning?.characterProfile?.ageRange || ""),
      appearance: String(planning?.characterProfile?.appearance || ""),
      wardrobe: String(planning?.characterProfile?.wardrobe || ""),
      personality: String(planning?.characterProfile?.personality || ""),
      speakingStyle: String(planning?.characterProfile?.speakingStyle || ""),
    },
    sceneDetails: String(planning?.sceneDetails || ""),
    firstFramePrompt: String(planning?.firstFramePrompt || ""),
    secondFramePrompt: String(planning?.secondFramePrompt || ""),
    fullVideoPrompt: String(planning?.fullVideoPrompt || ""),
    captionsByPlatform,
  };
}

export default function UGCVideoCreator() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [jobs, setJobs] = useState<UGCVideoJob[]>([]);
  const [characters, setCharacters] = useState<UGCVideoCharacter[]>([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [creatingCharacter, setCreatingCharacter] = useState(false);
  const [startingGenerationJobId, setStartingGenerationJobId] = useState("");
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showQueuedModal, setShowQueuedModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    title: string;
  } | null>(null);
  const [confirmStartJob, setConfirmStartJob] = useState<UGCVideoJob | null>(null);
  const [reviewDrafts, setReviewDrafts] = useState<
    Record<string, UGCVideoReviewPayload>
  >({});
  const [planStatusFilter, setPlanStatusFilter] = useState("all");
  const [jobStatusFilter, setJobStatusFilter] = useState("all");
  const [workflowFilter, setWorkflowFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [characterForm, setCharacterForm] = useState<CreateUGCCharacterRequest>({
    appId: "",
    includeAppContext: true,
    aiMode: true,
    generateImage: true,
    creativeDirection: "",
    baseImagePrompt: "",
    imageModel: "gpt-image-2.5-flare",
    characterProfile: {
      name: "",
      ageRange: "",
      appearance: "",
      wardrobe: "",
      personality: "",
      speakingStyle: "",
    },
  });
  useToastStateSync({ success, error });

  const [form, setForm] = useState<CreateUGCVideoJobRequest>({
    appId: "",
    objective: OBJECTIVE_OPTIONS[0],
    gender: "female",
    hook: HOOK_OPTIONS[0],
    lengthSeconds: 10,
    creativeDirection: "",
    selectedCharacterId: "",
    workflow: "direct",
    videoProvider: "kling",
    videoModel: DEFAULT_KLING_VIDEO_MODEL,
    aspectRatio: "9:16",
  });

  const selectedVideoModels = useMemo(
    () => (form.videoProvider === "kling" ? KLING_VIDEO_MODELS : GEMINI_VIDEO_MODELS),
    [form.videoProvider],
  );
  const selectedDurationOptions = useMemo(
    () => getDurationOptions(form.videoProvider, form.videoModel),
    [form.videoModel, form.videoProvider],
  );
  const selectedDurationMeta = useMemo(
    () =>
      selectedDurationOptions.find((option) => option.seconds === form.lengthSeconds) ||
      null,
    [form.lengthSeconds, selectedDurationOptions],
  );
  const estimatedCost = useMemo(
    () => estimateJobCost(form.videoProvider, form.videoModel, form.lengthSeconds),
    [form.lengthSeconds, form.videoModel, form.videoProvider],
  );

  const selectedApp = useMemo(
    () => apps.find((item) => item.appId === form.appId) || null,
    [apps, form.appId],
  );
  const selectedPlanCharacter = useMemo(
    () =>
      characters.find((item) => item.characterId === form.selectedCharacterId) || null,
    [characters, form.selectedCharacterId],
  );
  const appCharacters = useMemo(
    () =>
      characters.filter((item) =>
        form.appId ? item.appId === form.appId : true,
      ),
    [characters, form.appId],
  );

  const planJobs = useMemo(
    () =>
      [...jobs].sort(
        (left, right) =>
          new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
      ),
    [jobs],
  );

  const filteredPlanJobs = useMemo(
    () =>
      planJobs.filter((job) => {
        if (planStatusFilter !== "all" && job.status !== planStatusFilter) return false;
        if (workflowFilter !== "all" && job.inputs.workflow !== workflowFilter) return false;
        if (providerFilter !== "all" && job.inputs.videoProvider !== providerFilter) return false;
        return passesDateFilter(job.createdAt, dateFilter);
      }),
    [dateFilter, planJobs, planStatusFilter, providerFilter, workflowFilter],
  );

  const groupedPlanJobs = useMemo(() => {
    const groups = new Map<string, UGCVideoJob[]>();
    filteredPlanJobs.forEach((job) => {
      const key = planDateLabel(job.createdAt);
      const existing = groups.get(key);
      if (existing) existing.push(job);
      else groups.set(key, [job]);
    });
    return Array.from(groups.entries());
  }, [filteredPlanJobs]);

  const generationJobs = useMemo(
    () =>
      [...jobs].sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      ),
    [jobs],
  );

  const filteredGenerationJobs = useMemo(
    () =>
      generationJobs.filter((job) => {
        if (jobStatusFilter !== "all" && job.status !== jobStatusFilter) return false;
        if (workflowFilter !== "all" && job.inputs.workflow !== workflowFilter) return false;
        if (providerFilter !== "all" && job.inputs.videoProvider !== providerFilter) return false;
        return passesDateFilter(job.updatedAt, dateFilter);
      }),
    [dateFilter, generationJobs, jobStatusFilter, providerFilter, workflowFilter],
  );

  const groupedGenerationJobs = useMemo(() => {
    const groups = new Map<string, UGCVideoJob[]>();
    filteredGenerationJobs.forEach((job) => {
      const key = planDateLabel(job.updatedAt);
      const existing = groups.get(key);
      if (existing) existing.push(job);
      else groups.set(key, [job]);
    });
    return Array.from(groups.entries());
  }, [filteredGenerationJobs]);

  const jobsForActiveTab = useMemo(() => {
    if (activeTab === 3) return filteredPlanJobs;
    if (activeTab === 4) return filteredGenerationJobs;
    return jobs;
  }, [activeTab, filteredGenerationJobs, filteredPlanJobs, jobs]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    setLoadingApps(true);
    try {
      const loadedApps = await AppService.getUserApps(user.email);
      setApps(loadedApps);
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load apps.");
    } finally {
      setLoadingApps(false);
    }
  }, [user?.email]);

  const loadJobs = useCallback(async () => {
    if (!user) return;
    setLoadingJobs(true);
    try {
      const loadedJobs = await UGCVideoService.listUGCVideoJobs(
        form.appId ? { appId: form.appId } : undefined,
      );
      setJobs(loadedJobs);
      setReviewDrafts((prev) => {
        const next = { ...prev };
        for (const job of loadedJobs) {
          if (job.status === "review_pending" && !next[job.jobId]) {
            next[job.jobId] = reviewFromJob(job);
          }
        }
        return next;
      });
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load UGC video jobs.");
    } finally {
      setLoadingJobs(false);
    }
  }, [form.appId, user]);

  const loadCharacters = useCallback(async () => {
    if (!user) return;
    setLoadingCharacters(true);
    try {
      const loadedCharacters = await UGCVideoService.listUGCVideoCharacters(
        form.appId ? { appId: form.appId } : undefined,
      );
      setCharacters(loadedCharacters);
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load UGC characters.");
    } finally {
      setLoadingCharacters(false);
    }
  }, [form.appId, user]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  useEffect(() => {
    setCharacterForm((prev) => ({ ...prev, appId: form.appId || prev.appId || "" }));
  }, [form.appId]);

  useEffect(() => {
    if (!showQueuedModal) return;
    const timeout = window.setTimeout(() => setShowQueuedModal(false), 2600);
    return () => window.clearTimeout(timeout);
  }, [showQueuedModal]);

  useEffect(() => {
    if (!selectedDurationOptions.length) return;
    const isValid = selectedDurationOptions.some(
      (option) => option.seconds === form.lengthSeconds,
    );
    if (isValid) return;
    setForm((prev) => ({
      ...prev,
      lengthSeconds: selectedDurationOptions[0].seconds,
    }));
  }, [form.lengthSeconds, selectedDurationOptions]);

  useEffect(() => {
    if (!form.selectedCharacterId) return;
    const exists = appCharacters.some(
      (character) => character.characterId === form.selectedCharacterId,
    );
    if (exists) return;
    setForm((prev) => ({ ...prev, selectedCharacterId: "" }));
  }, [appCharacters, form.selectedCharacterId]);

  const handleCreatePlan = async () => {
    setError(null);
    setSuccess(null);
    if (!form.appId) {
      setError("Select a registered app.");
      return;
    }
    setCreatingPlan(true);
    try {
      const response = await UGCVideoService.createUGCVideoPlan(form);
      setSuccess(response.message || "UGC plan generated.");
      setActiveTab(3);
      await loadJobs();
    } catch (createError: any) {
      setError(createError?.message || "Failed to create UGC plan.");
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleStartGeneration = async (job: UGCVideoJob) => {
    const review = reviewDrafts[job.jobId];
    if (!review) {
      setError("No review payload available for this job.");
      return;
    }
    setStartingGenerationJobId(job.jobId);
    try {
      const response = await UGCVideoService.startUGCVideoGeneration({
        jobId: job.jobId,
        review,
        workflow: job.inputs.workflow,
        videoProvider: job.inputs.videoProvider,
        videoModel: job.inputs.videoModel,
        aspectRatio: job.inputs.aspectRatio,
      });
      setSuccess(response.message || "UGC generation queued.");
      setShowQueuedModal(true);
      setActiveTab(4);
      await loadJobs();
    } catch (startError: any) {
      setError(startError?.message || "Failed to start generation.");
    } finally {
      setStartingGenerationJobId("");
    }
  };

  const handleCopyCaption = async (platform: string, caption: string) => {
    const label = PLATFORM_META[platform]?.label || platform;
    try {
      await navigator.clipboard.writeText(caption);
      setSuccess(`${label} caption copied.`);
    } catch {
      setError("Failed to copy caption.");
    }
  };

  const handleDownloadVideo = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const handleCreateCharacter = async () => {
    setError(null);
    setSuccess(null);
    const appId = characterForm.appId || form.appId;
    if (!appId) {
      setError("Select a registered app for character creation.");
      return;
    }
    setCreatingCharacter(true);
    try {
      const payload: CreateUGCCharacterRequest = {
        ...characterForm,
        appId,
      };
      const response = await UGCVideoService.createUGCVideoCharacter(payload);
      setSuccess(response.message || "Character created.");
      await loadCharacters();
      setActiveTab(2);
    } catch (createError: any) {
      setError(createError?.message || "Failed to create character.");
    } finally {
      setCreatingCharacter(false);
    }
  };

  const handleApplyCharacterToPlan = (character: UGCVideoCharacter) => {
    setForm((prev) => ({
      ...prev,
      appId: character.appId || prev.appId,
      selectedCharacterId: character.characterId,
    }));
    setSuccess("Character linked to Create Plan.");
    setActiveTab(0);
  };

  const handleDuplicatePlan = async (job: UGCVideoJob) => {
    setError(null);
    setSuccess(null);
    setCreatingPlan(true);
    try {
      const payload: CreateUGCVideoJobRequest = {
        appId: job.appId,
        objective: job.inputs.objective,
        gender: job.inputs.gender,
        hook: job.inputs.hook,
        lengthSeconds: job.inputs.lengthSeconds,
        creativeDirection: job.inputs.creativeDirection || "",
        selectedCharacterId: job.inputs.selectedCharacterId || "",
        workflow: job.inputs.workflow,
        videoProvider: job.inputs.videoProvider,
        videoModel: job.inputs.videoModel,
        aspectRatio: job.inputs.aspectRatio,
      };
      const response = await UGCVideoService.createUGCVideoPlan(payload);
      setSuccess(response.message || "Plan duplicated.");
      await loadJobs();
      setActiveTab(3);
    } catch (duplicateError: any) {
      setError(duplicateError?.message || "Failed to duplicate plan.");
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleUseAsTemplate = (job: UGCVideoJob) => {
    setForm((prev) => ({
      ...prev,
      appId: job.appId || prev.appId,
      objective: job.inputs.objective,
      gender: job.inputs.gender,
      hook: job.inputs.hook,
      lengthSeconds: job.inputs.lengthSeconds,
      creativeDirection: job.inputs.creativeDirection || "",
      selectedCharacterId: job.inputs.selectedCharacterId || "",
      workflow: job.inputs.workflow,
      videoProvider: job.inputs.videoProvider,
      videoModel: job.inputs.videoModel,
      aspectRatio: job.inputs.aspectRatio,
    }));
    setActiveTab(0);
    setSuccess("Plan loaded into Create Plan as a template.");
  };

  const handlePreviewImage = (url: string, title: string) => {
    if (!url) return;
    setPreviewImage({ url, title });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2.5 }}>
      <Card
        sx={{
          mb: 2,
          borderRadius: 2.5,
          background:
            "linear-gradient(125deg, rgba(9,27,66,1) 0%, rgba(21,50,102,1) 45%, rgba(38,115,176,1) 100%)",
          color: "white",
        }}
      >
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            UGC Video Creator
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.75, opacity: 0.9 }}>
            Create plans automatically, review them by app, then run generation
            jobs when ready.
          </Typography>
          <Box sx={{ mt: 1.2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`${planJobs.length} plans`}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
            />
            <Chip
              size="small"
              label={`${characters.length} characters`}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
            />
            <Chip
              size="small"
              label={`${generationJobs.length} jobs`}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
            />
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab icon={<CampaignIcon />} iconPosition="start" label="Create Plan" />
          <Tab icon={<PersonAddIcon />} iconPosition="start" label="Character Creator" />
          <Tab icon={<PersonIcon />} iconPosition="start" label="Character Management" />
          <Tab icon={<AutoAwesomeIcon />} iconPosition="start" label="Video Plans" />
          <Tab icon={<MovieIcon />} iconPosition="start" label="Video Jobs" />
        </Tabs>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ py: "12px !important" }}>
          <FormControl fullWidth size="small">
            <InputLabel id="ugc-global-app-label">Registered App</InputLabel>
            <Select
              labelId="ugc-global-app-label"
              label="Registered App"
              value={form.appId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, appId: event.target.value }))
              }
              disabled={loadingApps}
            >
              <MenuItem value="">
                <em>Choose your app</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Grid container spacing={2.5}>
        {activeTab === 0 && (
          <>
            <Grid item xs={12} md={8}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Create Plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Every plan is auto-saved and tied to the selected registered app.
                  </Typography>

                  <Box sx={{ display: "grid", gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-existing-character-label">
                    Existing Character (Optional)
                  </InputLabel>
                  <Select
                    labelId="ugc-existing-character-label"
                    label="Existing Character (Optional)"
                    value={form.selectedCharacterId || ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        selectedCharacterId: String(event.target.value || ""),
                      }))
                    }
                    disabled={!form.appId}
                  >
                    <MenuItem value="">
                      <em>No saved character</em>
                    </MenuItem>
                    {appCharacters.map((character) => (
                      <MenuItem
                        key={character.characterId}
                        value={character.characterId}
                      >
                        {character.characterProfile.name || "Unnamed"} •{" "}
                        {character.characterProfile.ageRange || "N/A"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedPlanCharacter && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 1, borderRadius: 1.25, bgcolor: "rgba(2,132,199,0.04)" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Using saved character profile
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.3 }}>
                      {[
                        selectedPlanCharacter.characterProfile.name,
                        selectedPlanCharacter.characterProfile.ageRange,
                        selectedPlanCharacter.characterProfile.appearance,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </Typography>
                  </Paper>
                )}
                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-length-label">Video Length</InputLabel>
                  <Select
                    labelId="ugc-length-label"
                    label="Video Length"
                    value={String(form.lengthSeconds)}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        lengthSeconds: Number(event.target.value),
                      }))
                    }
                  >
                    {selectedDurationOptions.map((option) => (
                      <MenuItem key={`duration_${option.seconds}`} value={String(option.seconds)}>
                        {option.seconds} seconds
                        {option.native ? "" : " (Extension)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedDurationMeta?.native === false && (
                  <Alert severity="info" sx={{ mt: -0.5 }}>
                    This selection uses extension logic for{" "}
                    {form.videoProvider === "gemini" ? "Google Veo" : "the selected provider"}.
                    Initial clip is generated first, then extended to {form.lengthSeconds}s.
                  </Alert>
                )}
                <Typography variant="caption" color="text.secondary">
                  Supported lengths for this model:{" "}
                  {selectedDurationOptions.map((option) => `${option.seconds}s`).join(", ")}
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-workflow-label">Video Workflow</InputLabel>
                  <Select
                    labelId="ugc-workflow-label"
                    label="Video Workflow"
                    value={form.workflow}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        workflow: event.target.value as UGCVideoWorkflow,
                      }))
                    }
                  >
                    {VIDEO_WORKFLOW_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-provider-label">Final Video Provider</InputLabel>
                  <Select
                    labelId="ugc-provider-label"
                    label="Final Video Provider"
                    value={form.videoProvider}
                    onChange={(event) => {
                      const nextProvider = event.target.value as UGCVideoProvider;
                      const defaultModel =
                        nextProvider === "kling"
                          ? DEFAULT_KLING_VIDEO_MODEL
                          : DEFAULT_GEMINI_VIDEO_MODEL;
                      const durations = getDurationOptions(nextProvider, defaultModel);
                      setForm((prev) => ({
                        ...prev,
                        videoProvider: nextProvider,
                        videoModel: defaultModel,
                        lengthSeconds: durations[0]?.seconds || prev.lengthSeconds,
                      }));
                    }}
                  >
                    {VIDEO_PROVIDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-orientation-label">Orientation</InputLabel>
                  <Select
                    labelId="ugc-orientation-label"
                    label="Orientation"
                    value={String(form.aspectRatio || "9:16")}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        aspectRatio: event.target.value as "9:16" | "16:9" | "1:1",
                      }))
                    }
                  >
                    <MenuItem value="9:16">9:16 (Vertical)</MenuItem>
                    <MenuItem value="16:9">16:9 (Landscape)</MenuItem>
                    <MenuItem value="1:1">1:1 (Square)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-video-model-label">Final Video Model</InputLabel>
                  <Select
                    labelId="ugc-video-model-label"
                    label="Final Video Model"
                    value={form.videoModel}
                    onChange={(event) => {
                      const nextModel = String(event.target.value || "");
                      const durations = getDurationOptions(form.videoProvider, nextModel);
                      setForm((prev) => ({
                        ...prev,
                        videoModel: nextModel,
                        lengthSeconds: durations.some(
                          (option) => option.seconds === prev.lengthSeconds,
                        )
                          ? prev.lengthSeconds
                          : durations[0]?.seconds || prev.lengthSeconds,
                      }));
                    }}
                  >
                    {selectedVideoModels.map((model) => (
                      <MenuItem key={model.value} value={model.value}>
                        {model.label} • {model.costTag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-objective-label">Objective</InputLabel>
                  <Select
                    labelId="ugc-objective-label"
                    label="Objective"
                    value={form.objective}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        objective: event.target.value,
                      }))
                    }
                  >
                    {OBJECTIVE_OPTIONS.map((objective) => (
                      <MenuItem key={objective} value={objective}>
                        {objective}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-gender-label">Character Gender</InputLabel>
                  <Select
                    labelId="ugc-gender-label"
                    label="Character Gender"
                    value={form.gender}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        gender: event.target.value as UGCVideoGender,
                      }))
                    }
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="ugc-hook-label">Video Hook</InputLabel>
                  <Select
                    labelId="ugc-hook-label"
                    label="Video Hook"
                    value={form.hook}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, hook: event.target.value }))
                    }
                  >
                    {HOOK_OPTIONS.map((hook) => (
                      <MenuItem key={hook} value={hook}>
                        {hook}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Creative Direction (Optional)"
                  size="small"
                  multiline
                  minRows={3}
                  value={form.creativeDirection || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      creativeDirection: event.target.value,
                    }))
                  }
                />

                    <Button
                      variant="contained"
                      startIcon={<AutoAwesomeIcon />}
                      disabled={creatingPlan || !selectedApp}
                      onClick={handleCreatePlan}
                    >
                      {creatingPlan ? "Creating Plan..." : "Create Plan"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    background:
                      "linear-gradient(180deg, rgba(2,132,199,0.06) 0%, rgba(6,78,59,0.06) 100%)",
                  }}
                >
                  <CardContent sx={{ display: "grid", gap: 1.2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Workflow Summary
                    </Typography>
                    <Chip
                      size="small"
                      color={form.workflow === "direct" ? "success" : "info"}
                      label={
                        form.workflow === "direct"
                          ? "Direct workflow (faster, lower cost)"
                          : "Multi Frame workflow (higher control)"
                      }
                    />
                    <Divider />
                    <Typography variant="body2">
                      App: <strong>{selectedApp?.name || "No app selected"}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Character:{" "}
                      <strong>
                        {selectedPlanCharacter?.characterProfile?.name || "Auto-generated"}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      Provider/Model:{" "}
                      <strong>
                        {form.videoProvider === "kling" ? "Kling" : "Google Veo"} •{" "}
                        {form.videoModel}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      Duration: <strong>{form.lengthSeconds}s</strong>
                    </Typography>
                    <Typography variant="body2">
                      Orientation: <strong>{form.aspectRatio || "9:16"}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Estimated cost: <strong>{estimatedCost}</strong>
                    </Typography>
                    {form.workflow === "direct" ? (
                      <Alert severity="success" sx={{ mt: 0.5 }}>
                        Direct mode skips frame-image generation and sends prompt straight to the
                        selected video model.
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ mt: 0.5 }}>
                        Multi Frame mode generates first/second reference images, then creates
                        video for stronger visual continuity.
                      </Alert>
                    )}
                    {selectedDurationMeta?.native === false && (
                      <Alert severity="info">
                        {form.videoProvider === "gemini" ? "Google Veo" : "Selected provider"}{" "}
                        will generate a native clip first and then apply extension to reach{" "}
                        {form.lengthSeconds}s.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </>
        )}

        {activeTab === 1 && (
          <>
            <Grid item xs={12} md={8}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Character Creator
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    Build reusable character profiles and optionally generate the
                    reference portrait now.
                  </Typography>
                  <Box sx={{ display: "grid", gap: 1.2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="ugc-character-app-label">Registered App</InputLabel>
                      <Select
                        labelId="ugc-character-app-label"
                        label="Registered App"
                        value={characterForm.appId || ""}
                        onChange={(event) =>
                          setCharacterForm((prev) => ({
                            ...prev,
                            appId: String(event.target.value || ""),
                          }))
                        }
                        disabled={loadingApps}
                      >
                        <MenuItem value="">
                          <em>Choose your app</em>
                        </MenuItem>
                        {apps.map((app) => (
                          <MenuItem key={app.appId} value={app.appId}>
                            {app.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel id="ugc-character-image-model-label">
                        Character Image Model
                      </InputLabel>
                      <Select
                        labelId="ugc-character-image-model-label"
                        label="Character Image Model"
                        value={characterForm.imageModel || "gpt-image-2.5-flare"}
                        onChange={(event) =>
                          setCharacterForm((prev) => ({
                            ...prev,
                            imageModel: String(event.target.value || "gpt-image-2.5-flare"),
                          }))
                        }
                      >
                        {CHARACTER_IMAGE_MODELS.map((model) => (
                          <MenuItem key={model.value} value={model.value}>
                            {model.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip
                        clickable
                        color={characterForm.aiMode !== false ? "primary" : "default"}
                        label={`AI Mode: ${characterForm.aiMode !== false ? "On" : "Off"}`}
                        onClick={() =>
                          setCharacterForm((prev) => ({
                            ...prev,
                            aiMode: !(prev.aiMode !== false),
                          }))
                        }
                      />
                      <Chip
                        clickable
                        color={characterForm.includeAppContext !== false ? "primary" : "default"}
                        label={`Use App Context: ${
                          characterForm.includeAppContext !== false ? "On" : "Off"
                        }`}
                        onClick={() =>
                          setCharacterForm((prev) => ({
                            ...prev,
                            includeAppContext: !(prev.includeAppContext !== false),
                          }))
                        }
                      />
                      <Chip
                        clickable
                        color={characterForm.generateImage !== false ? "success" : "default"}
                        label={`Generate Image: ${
                          characterForm.generateImage !== false ? "On" : "Off"
                        }`}
                        onClick={() =>
                          setCharacterForm((prev) => ({
                            ...prev,
                            generateImage: !(prev.generateImage !== false),
                          }))
                        }
                      />
                    </Box>

                    <TextField
                      size="small"
                      label="Creative Direction (Optional)"
                      multiline
                      minRows={3}
                      value={characterForm.creativeDirection || ""}
                      onChange={(event) =>
                        setCharacterForm((prev) => ({
                          ...prev,
                          creativeDirection: event.target.value,
                        }))
                      }
                    />

                    <TextField
                      size="small"
                      label="Character Prompt (Optional if AI mode ON)"
                      multiline
                      minRows={4}
                      value={characterForm.baseImagePrompt || ""}
                      onChange={(event) =>
                        setCharacterForm((prev) => ({
                          ...prev,
                          baseImagePrompt: event.target.value,
                        }))
                      }
                    />

                    <Grid container spacing={1.2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Character Name"
                          value={characterForm.characterProfile?.name || ""}
                          onChange={(event) =>
                            setCharacterForm((prev) => ({
                              ...prev,
                              characterProfile: {
                                ...(prev.characterProfile || {}),
                                name: event.target.value,
                              },
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Age Range"
                          value={characterForm.characterProfile?.ageRange || ""}
                          onChange={(event) =>
                            setCharacterForm((prev) => ({
                              ...prev,
                              characterProfile: {
                                ...(prev.characterProfile || {}),
                                ageRange: event.target.value,
                              },
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Appearance"
                          value={characterForm.characterProfile?.appearance || ""}
                          onChange={(event) =>
                            setCharacterForm((prev) => ({
                              ...prev,
                              characterProfile: {
                                ...(prev.characterProfile || {}),
                                appearance: event.target.value,
                              },
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Wardrobe"
                          value={characterForm.characterProfile?.wardrobe || ""}
                          onChange={(event) =>
                            setCharacterForm((prev) => ({
                              ...prev,
                              characterProfile: {
                                ...(prev.characterProfile || {}),
                                wardrobe: event.target.value,
                              },
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Personality"
                          value={characterForm.characterProfile?.personality || ""}
                          onChange={(event) =>
                            setCharacterForm((prev) => ({
                              ...prev,
                              characterProfile: {
                                ...(prev.characterProfile || {}),
                                personality: event.target.value,
                              },
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Speaking Style"
                          value={characterForm.characterProfile?.speakingStyle || ""}
                          onChange={(event) =>
                            setCharacterForm((prev) => ({
                              ...prev,
                              characterProfile: {
                                ...(prev.characterProfile || {}),
                                speakingStyle: event.target.value,
                              },
                            }))
                          }
                        />
                      </Grid>
                    </Grid>

                    <Button
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      disabled={creatingCharacter || !characterForm.appId}
                      onClick={handleCreateCharacter}
                    >
                      {creatingCharacter ? "Creating Character..." : "Create Character"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  background:
                    "linear-gradient(180deg, rgba(6,95,70,0.06) 0%, rgba(2,132,199,0.06) 100%)",
                }}
              >
                <CardContent sx={{ display: "grid", gap: 1.2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Character Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saved characters are app-scoped and reusable in Create Plan.
                  </Typography>
                  <Typography variant="body2">
                    App: <strong>{apps.find((a) => a.appId === characterForm.appId)?.name || "Not selected"}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Mode: <strong>{characterForm.aiMode !== false ? "AI Generated" : "Manual"}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Image: <strong>{characterForm.generateImage !== false ? "Generated now" : "Profile only"}</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {activeTab === 2 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Character Management
                  </Typography>
                  <Button onClick={loadCharacters} disabled={loadingCharacters} size="small">
                    Refresh
                  </Button>
                </Box>
                {loadingCharacters ? (
                  <Box sx={{ display: "grid", gap: 1 }}>
                    <Skeleton variant="rounded" height={72} />
                    <Skeleton variant="rounded" height={72} />
                  </Box>
                ) : appCharacters.length === 0 ? (
                  <Alert severity="info">
                    No characters found for this app. Create one in Character Creator.
                  </Alert>
                ) : (
                  <Grid container spacing={1.5}>
                    {appCharacters.map((character) => (
                      <Grid item xs={12} md={6} key={character.characterId}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardContent sx={{ display: "grid", gap: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {character.characterProfile.name || "Unnamed character"}
                              </Typography>
                              <Chip
                                size="small"
                                color={character.status === "completed" ? "success" : "default"}
                                label={character.status.replace("_", " ")}
                              />
                            </Box>
                            {character.characterAsset?.downloadUrl && (
                              <Box sx={{ position: "relative", width: 140 }}>
                                <Box
                                  component="img"
                                  src={character.characterAsset.downloadUrl}
                                  alt={character.characterProfile.name || "Character"}
                                  onClick={() =>
                                    handlePreviewImage(
                                      character.characterAsset?.downloadUrl || "",
                                      character.characterProfile.name || "Character Asset",
                                    )
                                  }
                                  sx={{
                                    width: 140,
                                    height: 180,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    cursor: "zoom-in",
                                  }}
                                />
                              </Box>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {[
                                character.characterProfile.ageRange,
                                character.characterProfile.appearance,
                                character.characterProfile.wardrobe,
                              ]
                                .filter(Boolean)
                                .join(" • ")}
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Character Prompt
                              </Typography>
                              <Typography variant="body2">
                                {character.characterPrompt || "No prompt saved."}
                              </Typography>
                            </Paper>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleApplyCharacterToPlan(character)}
                              >
                                Use In Create Plan
                              </Button>
                              {character.characterAsset?.downloadUrl && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  onClick={() =>
                                    handleDownloadVideo(
                                      character.characterAsset?.downloadUrl || "",
                                    )
                                  }
                                >
                                  Download Asset
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {(activeTab === 3 || activeTab === 4) && (
          <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {activeTab === 3 ? "Video Plans" : "Video Jobs"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <FilterAltIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="ugc-filter-workflow-label">Workflow</InputLabel>
                    <Select
                      labelId="ugc-filter-workflow-label"
                      label="Workflow"
                      value={workflowFilter}
                      onChange={(event) => setWorkflowFilter(String(event.target.value))}
                    >
                      <MenuItem value="all">All workflows</MenuItem>
                      <MenuItem value="direct">Direct</MenuItem>
                      <MenuItem value="multi_frame">Multi Frame</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 128 }}>
                    <InputLabel id="ugc-filter-provider-label">Provider</InputLabel>
                    <Select
                      labelId="ugc-filter-provider-label"
                      label="Provider"
                      value={providerFilter}
                      onChange={(event) => setProviderFilter(String(event.target.value))}
                    >
                      <MenuItem value="all">All providers</MenuItem>
                      <MenuItem value="kling">Kling</MenuItem>
                      <MenuItem value="gemini">Google Veo</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="ugc-filter-date-label">Date</InputLabel>
                    <Select
                      labelId="ugc-filter-date-label"
                      label="Date"
                      value={dateFilter}
                      onChange={(event) => setDateFilter(String(event.target.value))}
                    >
                      {DATE_FILTER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 155 }}>
                    <InputLabel id="ugc-filter-status-label">Status</InputLabel>
                    <Select
                      labelId="ugc-filter-status-label"
                      label="Status"
                      value={activeTab === 3 ? planStatusFilter : jobStatusFilter}
                      onChange={(event) =>
                        activeTab === 3
                          ? setPlanStatusFilter(String(event.target.value))
                          : setJobStatusFilter(String(event.target.value))
                      }
                    >
                      {STATUS_FILTER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" onClick={loadJobs} disabled={loadingJobs}>
                    Refresh
                  </Button>
                </Box>
              </Box>

              {loadingJobs ? (
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Skeleton variant="rounded" height={64} />
                  <Skeleton variant="rounded" height={64} />
                  <Skeleton variant="rounded" height={64} />
                </Box>
              ) : jobsForActiveTab.length === 0 ? (
                <Alert severity="info">
                  {activeTab === 3
                    ? "No plans match current filters. Create a plan or adjust filters."
                    : "No jobs match current filters. Start generation or adjust filters."}
                </Alert>
              ) : activeTab === 3 ? (
                <Box sx={{ display: "grid", gap: 1 }}>
                  {groupedPlanJobs.map(([dateKey, dateJobs], groupIndex) => (
                    <Accordion key={dateKey} defaultExpanded={groupIndex === 0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            pr: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>{dateKey}</Typography>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`${dateJobs.length} plan${dateJobs.length > 1 ? "s" : ""}`}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {dateJobs.map((job) => (
                            <Paper
                              key={job.jobId}
                              variant="outlined"
                              sx={{ p: 1.2, borderRadius: 1.5 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {job.inputs.hook}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={statusLabel(job.status)}
                                  color={statusColor(job.status) as any}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                {new Date(job.createdAt).toLocaleString()}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                Objective: {job.inputs.objective}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {job.inputs.workflow === "multi_frame"
                                  ? "Multi Frame"
                                  : "Direct"}{" "}
                                •{" "}
                                {job.inputs.videoProvider === "kling"
                                  ? "Kling"
                                  : "Google Veo"}{" "}
                                • {job.inputs.videoModel} • {job.inputs.aspectRatio}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                              >
                                Est. cost:{" "}
                                {estimateJobCost(
                                  job.inputs.videoProvider,
                                  job.inputs.videoModel,
                                  job.inputs.lengthSeconds,
                                )}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.8 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PlaylistAddIcon />}
                                  onClick={() => handleUseAsTemplate(job)}
                                >
                                  Use As Template
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<RepeatIcon />}
                                  disabled={creatingPlan}
                                  onClick={() => handleDuplicatePlan(job)}
                                >
                                  Duplicate Plan
                                </Button>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: "grid", gap: 1.1 }}>
                  {groupedGenerationJobs.map(([dateKey, dateJobs], groupIndex) => (
                    <Accordion key={dateKey} defaultExpanded={groupIndex === 0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            pr: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>{dateKey}</Typography>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`${dateJobs.length} job${dateJobs.length > 1 ? "s" : ""}`}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: "grid", gap: 1.5 }}>
                          {dateJobs.map((job, jobIndex) => {
                    const captionEntries = Object.entries(
                      job.captionsByPlatform || {},
                    );
                    const review = reviewDrafts[job.jobId];
                    const firstFramePrompt =
                      job.report?.firstFramePromptUsed ||
                      job.planning?.firstFramePrompt ||
                      "";
                    const secondFramePrompt =
                      job.report?.secondFramePromptUsed ||
                      job.planning?.secondFramePrompt ||
                      "";
                    const videoPrompt =
                      job.report?.videoPromptUsed || job.planning?.fullVideoPrompt || "";
                    const firstFrameUrl =
                      job.report?.firstFrameAsset?.downloadUrl ||
                      job.frameAssets?.first?.downloadUrl ||
                      "";
                    const secondFrameUrl =
                      job.report?.secondFrameAsset?.downloadUrl ||
                      job.frameAssets?.second?.downloadUrl ||
                      "";
                    const videoUrl = job.videoAsset?.downloadUrl || "";
                    return (
                      <Accordion
                        key={job.jobId}
                        defaultExpanded={jobIndex === 0}
                        disabled={jobIndex === 0}
                        sx={{ borderRadius: 1.25 }}
                      >
                        <AccordionSummary
                          expandIcon={jobIndex === 0 ? null : <ExpandMoreIcon />}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              pr: 1,
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {job.appName || "App"} • {job.inputs.hook}
                            </Typography>
                            <Chip
                              size="small"
                              label={statusLabel(job.status)}
                              color={statusColor(job.status) as any}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0.5 }}>
                          <Card variant="outlined">
                        <CardContent sx={{ display: "grid", gap: 1.2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {job.appName || "App"} • {job.inputs.hook}
                            </Typography>
                            <Chip
                              size="small"
                              label={statusLabel(job.status)}
                              color={statusColor(job.status) as any}
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            {job.progress.message || "Waiting for update..."}
                          </Typography>
                          <Stepper activeStep={getJobStepIndex(job)} alternativeLabel>
                            {JOB_PHASE_STEPS.map((label) => (
                              <Step key={`${job.jobId}_${label}`}>
                                <StepLabel>{label}</StepLabel>
                              </Step>
                            ))}
                          </Stepper>
                          <LinearProgress
                            variant="determinate"
                            value={Math.max(0, Math.min(100, job.progress.percent))}
                          />
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1,
                              borderRadius: 1.25,
                              background:
                                "linear-gradient(180deg, rgba(15,23,42,0.01) 0%, rgba(15,23,42,0.04) 100%)",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Workflow
                            </Typography>
                            <Typography variant="body2">
                              {job.inputs.workflow === "direct" ? "Direct" : "Multi Frame"} •{" "}
                              {job.inputs.videoProvider === "kling" ? "Kling" : "Google Veo"} •{" "}
                              {job.inputs.videoModel} • {job.inputs.aspectRatio} •{" "}
                              {job.inputs.lengthSeconds}s •{" "}
                              {estimateJobCost(
                                job.inputs.videoProvider,
                                job.inputs.videoModel,
                                job.inputs.lengthSeconds,
                              )}
                            </Typography>
                          </Paper>

                          {(job.report?.characterProfileUsed || job.report?.sceneDetailsUsed) && (
                            <Paper
                              variant="outlined"
                              sx={{
                                borderRadius: 1.5,
                                p: 1.2,
                                display: "grid",
                                gap: 0.8,
                                background:
                                  "linear-gradient(180deg, rgba(6,78,59,0.02) 0%, rgba(6,78,59,0.05) 100%)",
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                Character & Scene
                              </Typography>
                              {job.report?.characterProfileUsed && (
                                <Typography variant="body2">
                                  {[
                                    job.report.characterProfileUsed.name,
                                    job.report.characterProfileUsed.ageRange,
                                    job.report.characterProfileUsed.appearance,
                                    job.report.characterProfileUsed.wardrobe,
                                    job.report.characterProfileUsed.personality,
                                    job.report.characterProfileUsed.speakingStyle,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </Typography>
                              )}
                              {job.report?.sceneDetailsUsed && (
                                <Typography variant="body2">{job.report.sceneDetailsUsed}</Typography>
                              )}
                            </Paper>
                          )}

                          {job.status === "review_pending" && review && (
                            <Box sx={{ display: "grid", gap: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Review & Edit
                              </Typography>
                              <TextField
                                size="small"
                                label="Character Name"
                                value={review.characterProfile.name}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      characterProfile: {
                                        ...review.characterProfile,
                                        name: event.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <TextField
                                size="small"
                                label="Character Age Range"
                                value={review.characterProfile.ageRange}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      characterProfile: {
                                        ...review.characterProfile,
                                        ageRange: event.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <TextField
                                size="small"
                                label="Character Appearance"
                                value={review.characterProfile.appearance}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      characterProfile: {
                                        ...review.characterProfile,
                                        appearance: event.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <TextField
                                size="small"
                                label="Character Wardrobe"
                                value={review.characterProfile.wardrobe}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      characterProfile: {
                                        ...review.characterProfile,
                                        wardrobe: event.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <TextField
                                size="small"
                                label="Character Personality"
                                value={review.characterProfile.personality}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      characterProfile: {
                                        ...review.characterProfile,
                                        personality: event.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <TextField
                                size="small"
                                label="Character Speaking Style"
                                value={review.characterProfile.speakingStyle}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      characterProfile: {
                                        ...review.characterProfile,
                                        speakingStyle: event.target.value,
                                      },
                                    },
                                  }))
                                }
                              />
                              <TextField
                                size="small"
                                label="Scene Details"
                                multiline
                                minRows={2}
                                value={review.sceneDetails}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      sceneDetails: event.target.value,
                                    },
                                  }))
                                }
                              />
                              {job.inputs.workflow === "multi_frame" && (
                                <>
                                  <TextField
                                    size="small"
                                    label="First Frame Prompt"
                                    multiline
                                    minRows={3}
                                    value={review.firstFramePrompt}
                                    onChange={(event) =>
                                      setReviewDrafts((prev) => ({
                                        ...prev,
                                        [job.jobId]: {
                                          ...review,
                                          firstFramePrompt: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                  <TextField
                                    size="small"
                                    label="Second Frame Prompt"
                                    multiline
                                    minRows={3}
                                    value={review.secondFramePrompt}
                                    onChange={(event) =>
                                      setReviewDrafts((prev) => ({
                                        ...prev,
                                        [job.jobId]: {
                                          ...review,
                                          secondFramePrompt: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </>
                              )}
                              <TextField
                                size="small"
                                label="Full Video Prompt"
                                multiline
                                minRows={4}
                                value={review.fullVideoPrompt}
                                onChange={(event) =>
                                  setReviewDrafts((prev) => ({
                                    ...prev,
                                    [job.jobId]: {
                                      ...review,
                                      fullVideoPrompt: event.target.value,
                                    },
                                  }))
                                }
                              />
                              {CAPTION_PLATFORMS.map((platform) => (
                                <TextField
                                  key={`${job.jobId}_${platform}_edit`}
                                  size="small"
                                  label={`${platform} Captions (one per line)`}
                                  multiline
                                  minRows={2}
                                  value={(review.captionsByPlatform[platform] || []).join(
                                    "\n",
                                  )}
                                  onChange={(event) =>
                                    setReviewDrafts((prev) => ({
                                      ...prev,
                                      [job.jobId]: {
                                        ...review,
                                        captionsByPlatform: {
                                          ...review.captionsByPlatform,
                                          [platform]: event.target.value
                                            .split("\n")
                                            .map((line) => line.trim())
                                            .filter(Boolean),
                                        },
                                      },
                                    }))
                                  }
                                />
                              ))}
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  borderRadius: 1.25,
                                  backgroundColor: "rgba(2,132,199,0.03)",
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                  Review Diff
                                </Typography>
                                {(() => {
                                  const draft = job.report?.promptDraft || job.planning;
                                  const rows: Array<{ label: string; draft: string; edited: string }> = [];
                                  const pushIfChanged = (
                                    label: string,
                                    draftValue: string,
                                    editedValue: string,
                                  ) => {
                                    if (textChanged(draftValue, editedValue)) {
                                      rows.push({
                                        label,
                                        draft: String(draftValue || ""),
                                        edited: String(editedValue || ""),
                                      });
                                    }
                                  };
                                  pushIfChanged(
                                    "Scene Details",
                                    String(draft?.sceneDetails || ""),
                                    String(review.sceneDetails || ""),
                                  );
                                  pushIfChanged(
                                    "Full Video Prompt",
                                    String(draft?.fullVideoPrompt || ""),
                                    String(review.fullVideoPrompt || ""),
                                  );
                                  if (job.inputs.workflow === "multi_frame") {
                                    pushIfChanged(
                                      "First Frame Prompt",
                                      String(draft?.firstFramePrompt || ""),
                                      String(review.firstFramePrompt || ""),
                                    );
                                    pushIfChanged(
                                      "Second Frame Prompt",
                                      String(draft?.secondFramePrompt || ""),
                                      String(review.secondFramePrompt || ""),
                                    );
                                  }
                                  return rows.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      No changes from generated draft.
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: "grid", gap: 1 }}>
                                      {rows.map((row) => (
                                        <Box key={`${job.jobId}_${row.label}`}>
                                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                            {row.label}
                                          </Typography>
                                          <Grid container spacing={1} sx={{ mt: 0.2 }}>
                                            <Grid item xs={12} md={6}>
                                              <Paper variant="outlined" sx={{ p: 0.8 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                  Generated
                                                </Typography>
                                                <Typography variant="body2">{row.draft}</Typography>
                                              </Paper>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                              <Paper variant="outlined" sx={{ p: 0.8 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                  Edited
                                                </Typography>
                                                <Typography variant="body2">{row.edited}</Typography>
                                              </Paper>
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                })()}
                              </Paper>
                              <Button
                                variant="contained"
                                disabled={startingGenerationJobId === job.jobId}
                                onClick={() => setConfirmStartJob(job)}
                              >
                                {startingGenerationJobId === job.jobId
                                  ? "Starting..."
                                  : "Start Generation"}
                              </Button>
                            </Box>
                          )}

                          {((job.inputs.workflow === "multi_frame" &&
                            (firstFramePrompt || secondFramePrompt)) ||
                            videoPrompt) && (
                            <Paper
                              variant="outlined"
                              sx={{
                                borderRadius: 1.5,
                                p: 1.2,
                                display: "grid",
                                gap: 1,
                                background:
                                  "linear-gradient(180deg, rgba(15,23,42,0.02) 0%, rgba(15,23,42,0.04) 100%)",
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                Prompt Suite
                              </Typography>
                              {job.inputs.workflow === "multi_frame" && firstFramePrompt && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    First Frame Prompt
                                  </Typography>
                                  <Typography variant="body2">{firstFramePrompt}</Typography>
                                </Box>
                              )}
                              {job.inputs.workflow === "multi_frame" && secondFramePrompt && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Second Frame Prompt
                                  </Typography>
                                  <Typography variant="body2">{secondFramePrompt}</Typography>
                                </Box>
                              )}
                              {videoPrompt && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Full Video Prompt
                                  </Typography>
                                  <Typography variant="body2">{videoPrompt}</Typography>
                                </Box>
                              )}
                            </Paper>
                          )}

                          {(firstFrameUrl || secondFrameUrl || videoUrl) && (
                            <Paper
                              variant="outlined"
                              sx={{
                                borderRadius: 1.5,
                                p: 1.2,
                                display: "grid",
                                gap: 1.2,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                Creative Artifacts
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
                                {firstFrameUrl && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      First Frame
                                    </Typography>
                                    <Box sx={{ position: "relative" }}>
                                      <Box
                                        component="img"
                                        src={firstFrameUrl}
                                        alt="First frame"
                                        onClick={() =>
                                          handlePreviewImage(firstFrameUrl, "First Frame")
                                        }
                                        sx={{
                                          width: 128,
                                          height: 176,
                                          objectFit: "cover",
                                          borderRadius: 1,
                                          border: "1px solid",
                                          borderColor: "divider",
                                          cursor: "zoom-in",
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handlePreviewImage(firstFrameUrl, "First Frame")
                                        }
                                        sx={{
                                          position: "absolute",
                                          top: 4,
                                          right: 4,
                                          bgcolor: "rgba(0,0,0,0.45)",
                                          color: "white",
                                          "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                                        }}
                                      >
                                        <OpenInFullIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                )}
                                {secondFrameUrl && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Second Frame
                                    </Typography>
                                    <Box sx={{ position: "relative" }}>
                                      <Box
                                        component="img"
                                        src={secondFrameUrl}
                                        alt="Second frame"
                                        onClick={() =>
                                          handlePreviewImage(secondFrameUrl, "Second Frame")
                                        }
                                        sx={{
                                          width: 128,
                                          height: 176,
                                          objectFit: "cover",
                                          borderRadius: 1,
                                          border: "1px solid",
                                          borderColor: "divider",
                                          cursor: "zoom-in",
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handlePreviewImage(secondFrameUrl, "Second Frame")
                                        }
                                        sx={{
                                          position: "absolute",
                                          top: 4,
                                          right: 4,
                                          bgcolor: "rgba(0,0,0,0.45)",
                                          color: "white",
                                          "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                                        }}
                                      >
                                        <OpenInFullIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                )}
                              </Box>

                              {videoUrl && (
                                <Box sx={{ pt: 0.5, display: "grid", gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Generated Video
                                  </Typography>
                                  <Box
                                    component="video"
                                    controls
                                    src={videoUrl}
                                    sx={{
                                      width: "100%",
                                      maxWidth: 420,
                                      borderRadius: 1.2,
                                      border: "1px solid",
                                      borderColor: "divider",
                                      backgroundColor: "black",
                                    }}
                                  />
                                  <Box>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DownloadIcon />}
                                      onClick={() => handleDownloadVideo(videoUrl)}
                                    >
                                      Download Video
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </Paper>
                          )}

                          {captionEntries.length > 0 && (
                            <Box sx={{ display: "grid", gap: 0.8, pt: 0.6 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Social Captions
                              </Typography>
                              <Grid container spacing={1}>
                                {captionEntries.map(([platform, captions]) => {
                                  const firstCaption = Array.isArray(captions)
                                    ? captions[0] || ""
                                    : "";
                                  const meta = PLATFORM_META[platform] || {
                                    label: platform,
                                    icon: MusicNoteIcon,
                                    accent: "#455A64",
                                  };
                                  const Icon = meta.icon;
                                  return (
                                    <Grid key={`${job.jobId}_${platform}`} item xs={12} md={6}>
                                      <Paper
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 1.25,
                                          p: 1,
                                          display: "grid",
                                          gap: 0.7,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                          }}
                                        >
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Avatar
                                              sx={{
                                                width: 22,
                                                height: 22,
                                                bgcolor: `${meta.accent}1A`,
                                                color: meta.accent,
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 14 }} />
                                            </Avatar>
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                              {meta.label}
                                            </Typography>
                                          </Box>
                                          {firstCaption && (
                                            <Tooltip title={`Copy ${meta.label} caption`}>
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleCopyCaption(platform, firstCaption)
                                                }
                                              >
                                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Box>
                                        <Typography variant="body2">
                                          {firstCaption || "No caption generated."}
                                        </Typography>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>
                          )}

                          {job.error && (
                            <Alert severity="error" sx={{ mt: 0.8 }}>
                              {job.error}
                            </Alert>
                          )}
                          </CardContent>
                          </Card>
                        </AccordionDetails>
                      </Accordion>
                    );
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
          </Grid>
        )}
      </Grid>

      <CreationProgressModal
        open={showQueuedModal}
        storeLabel="UGC Video"
        title="Generation queued"
        description="UGC video generation is queued. Use Refresh to check latest status."
      />
      <Dialog
        open={Boolean(confirmStartJob)}
        onClose={() => setConfirmStartJob(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Generation</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Review final generation settings before queueing this job.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.25 }}>
            <Typography variant="body2">
              Workflow:{" "}
              <strong>
                {confirmStartJob?.inputs.workflow === "multi_frame"
                  ? "Multi Frame"
                  : "Direct"}
              </strong>
            </Typography>
            <Typography variant="body2">
              Provider/Model:{" "}
              <strong>
                {confirmStartJob?.inputs.videoProvider === "kling" ? "Kling" : "Google Veo"} •{" "}
                {confirmStartJob?.inputs.videoModel}
              </strong>
            </Typography>
            <Typography variant="body2">
              Duration: <strong>{confirmStartJob?.inputs.lengthSeconds || 0}s</strong>
            </Typography>
            <Typography variant="body2">
              Orientation: <strong>{confirmStartJob?.inputs.aspectRatio || "9:16"}</strong>
            </Typography>
            <Typography variant="body2">
              Estimated Cost:{" "}
              <strong>
                {confirmStartJob
                  ? estimateJobCost(
                      confirmStartJob.inputs.videoProvider,
                      confirmStartJob.inputs.videoModel,
                      confirmStartJob.inputs.lengthSeconds,
                    )
                  : "-"}
              </strong>
            </Typography>
          </Paper>
          <TextField
            label="Final Prompt Preview"
            size="small"
            multiline
            minRows={5}
            value={
              confirmStartJob
                ? reviewDrafts[confirmStartJob.jobId]?.fullVideoPrompt ||
                  confirmStartJob.planning?.fullVideoPrompt ||
                  ""
                : ""
            }
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStartJob(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!confirmStartJob || startingGenerationJobId === confirmStartJob.jobId}
            onClick={async () => {
              if (!confirmStartJob) return;
              setConfirmStartJob(null);
              await handleStartGeneration(confirmStartJob);
            }}
          >
            {confirmStartJob && startingGenerationJobId === confirmStartJob.jobId
              ? "Starting..."
              : "Start Generation"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{previewImage?.title || "Frame Preview"}</DialogTitle>
        <DialogContent>
          {previewImage?.url && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.title || "Frame preview"}
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
